import { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Copy, Inbox, Undo2, FileText, Users, Calculator } from 'lucide-react';
import {
  Drawer, Card, Typography, Button, Row, Col, Tag, Space, Divider, Popconfirm, Descriptions, EmptyState, Alert, message,
  Form, Input, InputNumber, DatePicker, Select, Switch,
} from '../../ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { request, api } from '../../api/client';

async function openSchemePdf(id: string, retailerId?: string) {
  const r = await api.get(`/discount-schemes/${id}/acceptance-pdf`, { params: retailerId ? { retailerId } : {}, responseType: 'blob' });
  window.open(URL.createObjectURL(r.data), '_blank');
}
import { usePermission } from '../../store/auth';
import { TwDataTable, Pill, type DataTableColumn } from '../../components/TwDataTable';

// Lifecycle: DRAFT (parked) → INACTIVE (on the shelf, ready) ⇄ ACTIVE (the single live scheme) ; ARCHIVED (retired).
const STATUS_COLOR: Record<string, string> = { DRAFT: 'gold', INACTIVE: 'blue', ACTIVE: 'green', ARCHIVED: 'default' };
const STATUS_LABEL: Record<string, string> = { DRAFT: 'Draft', INACTIVE: 'Inactive', ACTIVE: 'Active', ARCHIVED: 'Archived' };
const FREQ_LABEL: Record<string, string> = { WEEKLY: 'Weekly', BIWEEKLY: 'Biweekly', MONTHLY: 'Monthly' };
const inr = (v: any) => `₹${Number(v ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const amountProps = {
  style: { width: '100%' },
  min: 0,
  step: 100000,
  formatter: (v: any) => (v == null || v === '' ? '' : `₹${Number(v).toLocaleString('en-IN')}`),
  parser: (v: any) => Number(String(v ?? '').replace(/[^\d.]/g, '')) as any,
};

// The company's current structure — pre-filled on "New scheme" so it's a starting point to tweak.
const SEED = {
  financialYear: '2026-27',
  creditPeriodDays: 90,
  overdueInterestPct: 2,
  remindersEnabled: true,
  reminderFrequency: 'WEEKLY',
  advanceSlabBasis: 'BASIC_TAXABLE_VALUE',
  advanceCdSlabs: [{ minAmount: 500000, cdPct: 7.5 }, { minAmount: 750000, cdPct: 10 }, { minAmount: 1000000, cdPct: 12.5 }],
  timelineCdSlabs: [{ maxDays: 0, cdPct: 10 }, { maxDays: 15, cdPct: 5 }, { maxDays: 30, cdPct: 2.5 }],
  todSlabs: [
    { fromAmount: 0, toAmount: 2000000, basePct: 0, perLakhPct: 1, capPct: 20 },
    { fromAmount: 2000000, toAmount: 3000000, basePct: 20, perLakhPct: 0.5, capPct: 25 },
    { fromAmount: 3000000, toAmount: null, basePct: 25, perLakhPct: 0, capPct: 25 },
  ],
};

export function CommercialTermsPage() {
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [assignFor, setAssignFor] = useState<any>(null);
  const canManage = usePermission('COMMERCIAL.MANAGE');
  const canAssign = usePermission('COMMERCIAL.ASSIGN');
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['commercial-schemes'],
    queryFn: () => request<any[]>({ url: '/discount-schemes' }),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['commercial-schemes'] });
  const act = (url: string, method: 'POST' | 'DELETE', okMsg: string, body?: any) =>
    request({ url, method, data: body }).then(() => { message.success(okMsg); invalidate(); })
      .catch((e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'));


  const columns: DataTableColumn<any>[] = [
    { key: 'name', title: 'Scheme', dataIndex: 'name', sortable: true, alwaysVisible: true,
      render: (v: string, r: any) => (
        <div className="flex items-center gap-2">
          <span className="font-medium text-content">{v}</span>
          {r.version > 1 && <Pill>v{r.version}</Pill>}
        </div>
      ) },
    { key: 'financialYear', title: 'FY', dataIndex: 'financialYear', sortable: true,
      render: (v: string) => <span className="text-muted">{v}</span> },
    { key: 'status', title: 'Status', dataIndex: 'status', sortable: true,
      render: (v: string) => <Pill color={STATUS_COLOR[v] ?? 'default'}>{STATUS_LABEL[v] ?? v}</Pill> },
    { key: 'dealerCount', title: 'Dealers', dataIndex: 'dealerCount', align: 'right',
      render: (v: number) => <span className="tabular-nums">{v || 0}</span> },
    { key: 'reminders', title: 'Reminders', dataIndex: 'remindersEnabled',
      render: (_: any, r: any) => (r.remindersEnabled
        ? <Pill color="green">{FREQ_LABEL[r.reminderFrequency] ?? 'Weekly'}</Pill>
        : <span className="text-subtle">Off</span>) },
    { key: 'effectiveFrom', title: 'From', dataIndex: 'effectiveFrom',
      render: (v: any) => <span className="tabular-nums text-muted">{v}</span> },
    { key: 'effectiveTo', title: 'To', dataIndex: 'effectiveTo',
      render: (v: any) => v ? <span className="tabular-nums text-muted">{v}</span> : <Pill color="green">Open</Pill> },
    { key: 'creditPeriodDays', title: 'Credit', dataIndex: 'creditPeriodDays', align: 'right', render: (v: any) => <span className="tabular-nums">{v}d</span> },
    { key: 'createdAt', title: 'Created', dataIndex: 'createdAt', sortable: true, alwaysVisible: true,
      render: (v: any) => v
        ? <span className="tabular-nums text-muted" title={dayjs(v).format('DD MMM YYYY, HH:mm')}>{dayjs(v).format('DD MMM YYYY')}</span>
        : <span className="text-subtle">—</span> },
    {
      key: 'actions', title: '', alwaysVisible: true, width: 280,
      render: (_: any, r: any) => (
        <div onClick={(e) => e.stopPropagation()}>
          <Space size={4}>
            {/* Live toggle FIRST (shelf rows only): flips the single live scheme INACTIVE⇄ACTIVE. Default (not small)
                size so it stands taller than the icon buttons and reads as the primary row control. */}
            {canManage && (r.status === 'ACTIVE' || r.status === 'INACTIVE') && (
              <Switch
                checkedChildren="Live" unCheckedChildren="Off" checked={r.status === 'ACTIVE'} className="mr-1"
                title={r.status === 'ACTIVE' ? 'Live scheme — turn off to shelve it' : 'Turn on to make this the single live scheme'}
                onCheckedChange={(on) => act(`/discount-schemes/${r.id}/status`, 'POST', on ? 'Activated — now the live scheme' : 'Deactivated', { status: on ? 'ACTIVE' : 'INACTIVE' })}
              />
            )}
            <Button variant="text" size="sm" icon={<FileText size={16} />} title="Download PDF" aria-label="Download PDF"
              onClick={() => openSchemePdf(r.id).catch(() => message.error('Failed to open PDF'))} />
            {canManage && (
              <Button variant="text" size="sm" icon={<Pencil size={16} />} title="Edit" aria-label="Edit" onClick={() => { setEditingId(r.id); setOpen(true); }} />
            )}
            {/* Draft/Archive take a shelf row off the shelf; off-shelf rows (DRAFT / ARCHIVED, via their KPI) get Restore. */}
            {canManage && (r.status === 'ACTIVE' || r.status === 'INACTIVE') && (
              <>
                <Button variant="text" size="sm" icon={<FileText size={16} />} title="Move to draft (park it off the shelf)" aria-label="Move to draft (park it off the shelf)"
                  onClick={() => act(`/discount-schemes/${r.id}/status`, 'POST', 'Moved to draft', { status: 'DRAFT' })} />
                <Button variant="text" size="sm" icon={<Inbox size={16} />} title="Archive (retire it off the shelf)" aria-label="Archive (retire it off the shelf)"
                  onClick={() => act(`/discount-schemes/${r.id}/status`, 'POST', 'Archived', { status: 'ARCHIVED' })} />
              </>
            )}
            {canManage && (r.status === 'DRAFT' || r.status === 'ARCHIVED') && (
              <Button variant="ghost" size="sm" icon={<Undo2 size={16} />} title="Restore to the shelf (Inactive)"
                onClick={() => act(`/discount-schemes/${r.id}/status`, 'POST', 'Restored to the shelf', { status: 'INACTIVE' })}>Restore</Button>
            )}
            {canManage && (
              <Button variant="text" size="sm" icon={<Copy size={16} />} title="Clone to a new draft" aria-label="Clone to a new draft" onClick={() => act(`/discount-schemes/${r.id}/clone`, 'POST', 'Cloned to a new draft')} />
            )}
            {canAssign && (
              <Button variant="text" size="sm" icon={<Users size={16} />} title="Assign dealers" aria-label="Assign dealers" onClick={() => setAssignFor(r)} />
            )}
            {canManage && (
              <Popconfirm
                title={r.status === 'ACTIVE' ? 'Delete this LIVE scheme?' : 'Delete this scheme?'}
                description={r.status === 'ACTIVE' ? 'It will be archived and removed (recoverable).' : undefined}
                okText="Delete" okButtonProps={{ danger: true }}
                onConfirm={() => act(`/discount-schemes/${r.id}`, 'DELETE', 'Deleted')}>
                <Button variant="text" size="sm" icon={<Trash2 size={16} className="text-danger-600 dark:text-danger-500" />} title="Delete" aria-label="Delete" />
              </Popconfirm>
            )}
          </Space>
        </div>
      ),
    },
  ];

  const schemes = data ?? [];
  const activeCount = schemes.filter((s: any) => s.status === 'ACTIVE').length;
  const inactiveCount = schemes.filter((s: any) => s.status === 'INACTIVE').length;
  const draftCount = schemes.filter((s: any) => s.status === 'DRAFT').length;
  const archivedCount = schemes.filter((s: any) => s.status === 'ARCHIVED').length;
  const shelfCount = activeCount + inactiveCount; // the main list = schemes on the shelf (live + ready)
  const dealersCovered = schemes.reduce((n: number, s: any) => n + Number(s.dealerCount || 0), 0);

  // Click a KPI to filter the table; click it again (or the "Schemes" total) to return to the shelf.
  // Default ('ALL') = the shelf (ACTIVE + INACTIVE); Drafts/Archived are off-shelf, reached only via their KPI.
  const [filter, setFilter] = useState<'ALL' | 'ACTIVE' | 'DRAFT' | 'ARCHIVED' | 'DEALERS'>('ALL');
  const toggle = (f: 'ACTIVE' | 'DRAFT' | 'ARCHIVED' | 'DEALERS') => setFilter((cur) => (cur === f ? 'ALL' : f));
  const filtered = schemes.filter((s: any) =>
    filter === 'ACTIVE' ? s.status === 'ACTIVE'
      : filter === 'DRAFT' ? s.status === 'DRAFT'
        : filter === 'ARCHIVED' ? s.status === 'ARCHIVED'
          : filter === 'DEALERS' ? Number(s.dealerCount || 0) > 0
            : (s.status === 'ACTIVE' || s.status === 'INACTIVE')); // 'ALL' = the shelf

  return (
    <div>
      {/* Header row — page title + primary actions, above the filters. */}
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-semibold text-content">Commercial Terms &amp; Discount Structure</h1>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {canManage ? (
            <Button variant="primary" size="sm" icon={<Plus size={16} />} onClick={() => { setEditingId(null); setOpen(true); }}>New scheme</Button>
          ) : undefined}
        </div>
      </div>
      <p className="mt-1 max-w-3xl text-sm text-muted">
        Dealer cash discount (advance &amp; prompt-payment), credit terms and turnover discount, per financial year. Distinct from product promo schemes.
      </p>

      <div className="mb-4 mt-4 grid max-w-3xl grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <Tile label="On shelf" value={shelfCount} active={filter === 'ALL'} onClick={() => setFilter('ALL')} />
        <Tile label="Active" value={activeCount} accent="text-brand-700" active={filter === 'ACTIVE'} onClick={() => toggle('ACTIVE')} />
        <Tile label="Drafts" value={draftCount} accent="text-harvest-700" active={filter === 'DRAFT'} onClick={() => toggle('DRAFT')} />
        <Tile label="Archived" value={archivedCount} accent="text-muted" active={filter === 'ARCHIVED'} onClick={() => toggle('ARCHIVED')} />
        <Tile label="Dealers covered" value={dealersCovered} accent="text-sky-600" active={filter === 'DEALERS'} onClick={() => toggle('DEALERS')} />
      </div>

      <TwDataTable
        storageKey="commercial-schemes"
        rowKey="id"
        loading={isLoading}
        dataSource={filtered}
        columns={columns}
        pagination={{ pageSize: 25, showSizeChanger: true }}
        emptyText={filter === 'ALL'
          ? 'No schemes on the shelf — create one, or restore one from Drafts / Archived.'
          : filter === 'DRAFT' ? 'No drafts — parked schemes show here. Restore one to put it back on the shelf.'
            : filter === 'ARCHIVED' ? 'No archived schemes — retired schemes show here. Restore one to bring it back.'
              : 'No schemes match this filter.'}
        toolbarLeft={
          <span className="text-sm text-muted">
            {filter === 'ALL'
              ? `${shelfCount} on the shelf${activeCount ? ' · 1 live' : ' · none live'}`
              : <>Showing <b className="text-content">{filtered.length}</b> of {schemes.length} · <button className="text-brand-700 hover:underline dark:text-brand-400" onClick={() => setFilter('ALL')}>back to shelf</button></>}
          </span>
        }
      />

      <SchemeBuilder open={open} schemeId={editingId} onClose={() => { setOpen(false); setEditingId(null); }} onSaved={invalidate} />
      <AssignDrawer scheme={assignFor} onClose={() => setAssignFor(null)} onChanged={invalidate} />
    </div>
  );
}

function Tile({ label, value, accent = 'text-content', active = false, onClick }: { label: string; value: number; accent?: string; active?: boolean; onClick?: () => void }) {
  return (
    <button
      type="button" onClick={onClick}
      className={`rounded-card border bg-surface p-3 text-left shadow-soft transition hover:border-brand-600/50 ${active ? 'border-brand-600 ring-1 ring-brand-600/30' : 'border-line'}`}
    >
      <div className="text-xs text-muted">{label}</div>
      <div className={`text-2xl font-semibold tabular-nums ${accent}`}>{value}</div>
    </button>
  );
}

// ─────────────────────────── Builder ───────────────────────────

function SchemeBuilder({ open, schemeId, onClose, onSaved }: { open: boolean; schemeId: string | null; onClose: () => void; onSaved: () => void }) {
  const [form] = Form.useForm();
  const isEdit = !!schemeId;

  const { data: existing } = useQuery({
    queryKey: ['commercial-scheme', schemeId],
    queryFn: () => request<any>({ url: `/discount-schemes/${schemeId}` }),
    enabled: open && !!schemeId,
  });
  // Dealer list for the inline "Assigned dealers" picker.
  const { data: dealers } = useQuery({
    queryKey: ['retailers-picker'],
    queryFn: async () => {
      const r: any = await request<any>({ url: '/retailers', params: { pageSize: 1000 } });
      return Array.isArray(r) ? r : (r?.data ?? []);
    },
    enabled: open,
  });

  useEffect(() => {
    if (!open) return;
    if (isEdit && existing) {
      form.setFieldsValue({
        name: existing.name, financialYear: existing.financialYear,
        effectiveFrom: existing.effectiveFrom ? dayjs(existing.effectiveFrom) : null,
        effectiveTo: existing.effectiveTo ? dayjs(existing.effectiveTo) : null,
        creditPeriodDays: existing.creditPeriodDays, overdueInterestPct: Number(existing.overdueInterestPct ?? 0), notes: existing.notes,
        remindersEnabled: existing.remindersEnabled === undefined ? true : !!existing.remindersEnabled,
        reminderFrequency: existing.reminderFrequency ?? 'WEEKLY',
        advanceSlabBasis: existing.advanceSlabBasis ?? 'BASIC_TAXABLE_VALUE',
        advanceCdSlabs: (existing.advanceCdSlabs ?? []).map((s: any) => ({ minAmount: Number(s.minAmount), cdPct: Number(s.cdPct) })),
        timelineCdSlabs: (existing.timelineCdSlabs ?? []).map((s: any) => ({ maxDays: s.maxDays, cdPct: Number(s.cdPct) })),
        todSlabs: (existing.todSlabs ?? []).map((s: any) => ({ fromAmount: Number(s.fromAmount), toAmount: s.toAmount != null ? Number(s.toAmount) : null, basePct: Number(s.basePct), perLakhPct: Number(s.perLakhPct), capPct: Number(s.capPct) })),
        // Pre-fill the current assignment set so saving reconciles to it (no accidental removal).
        assignedRetailerIds: (existing.assignments ?? []).map((a: any) => String(a.retailerId)),
      });
    } else if (!isEdit) {
      form.resetFields();
      form.setFieldsValue({ effectiveFrom: dayjs('2026-04-01'), assignedRetailerIds: [], ...SEED });
    }
  }, [open, isEdit, existing, form]);

  const save = useMutation({
    mutationFn: (vals: any) => {
      const payload = {
        name: vals.name, financialYear: vals.financialYear,
        effectiveFrom: vals.effectiveFrom?.format('YYYY-MM-DD'),
        effectiveTo: vals.effectiveTo ? vals.effectiveTo.format('YYYY-MM-DD') : null,
        creditPeriodDays: vals.creditPeriodDays ?? 0, overdueInterestPct: vals.overdueInterestPct ?? 0, notes: vals.notes,
        remindersEnabled: vals.remindersEnabled ?? true, reminderFrequency: vals.reminderFrequency ?? 'WEEKLY',
        advanceSlabBasis: vals.advanceSlabBasis ?? 'BASIC_TAXABLE_VALUE',
        advanceCdSlabs: (vals.advanceCdSlabs ?? []).filter((s: any) => s?.minAmount != null),
        timelineCdSlabs: (vals.timelineCdSlabs ?? []).filter((s: any) => s?.maxDays != null),
        todSlabs: (vals.todSlabs ?? []).filter((s: any) => s?.fromAmount != null).map((s: any) => ({ ...s, toAmount: s.toAmount ?? null })),
        // Reconcile assignments to exactly this list. Only send when the field is initialised (avoids
        // wiping assignments if the form is submitted before it loads on edit).
        ...(Array.isArray(vals.assignedRetailerIds) ? { assignedRetailerIds: vals.assignedRetailerIds.map(String) } : {}),
      };
      return isEdit ? request({ url: `/discount-schemes/${schemeId}`, method: 'PUT', data: payload }) : request({ url: '/discount-schemes', method: 'POST', data: payload });
    },
    onSuccess: () => { message.success(isEdit ? 'Scheme updated' : 'Scheme created — added to the shelf'); onSaved(); onClose(); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to save'),
  });

  return (
    <Drawer
      open={open} onClose={onClose} width={920}
      title={isEdit ? `Edit scheme${existing?.status ? ` · ${STATUS_LABEL[existing.status] ?? existing.status}` : ''}` : 'New commercial scheme'}
      extra={<Space><Button variant="default" onClick={onClose}>Cancel</Button><Button variant="primary" loading={save.isPending} onClick={() => form.submit()}>{isEdit ? 'Save changes' : 'Create scheme'}</Button></Space>}
    >
      <Form form={form} layout="vertical" onFinish={(v) => {
        // Auto-arrange the TOD bands (sort by From + snap contiguous upper bounds) so the user needn't order them
        // by hand. The reorder is written back to the form so it's visible; a genuine same-lower-bound clash stops
        // the save with a precise message.
        const tidy = tidyTodBands(v.todSlabs);
        form.setFieldValue('todSlabs', tidy.todSlabs);
        if (tidy.error) { message.error(tidy.error); return; }
        save.mutate({ ...v, todSlabs: tidy.todSlabs });
      }}>
        {isEdit && existing?.status === 'ACTIVE' && (
          <Alert type="warning" className="mb-3"
            title="Editing a live scheme"
            description="Changes apply to future discounts & reminders only — past discount adjustments are unaffected. To keep the current version intact, use Clone instead." />
        )}
        <Row gutter={[12, 12]}>
          <Col span={10}><Form.Item name="name" label="Scheme name" rules={[{ required: true }]}><Input placeholder="Dealer Discount Structure FY 2026-27" /></Form.Item></Col>
          <Col span={5}><Form.Item name="financialYear" label="Financial year" rules={[{ required: true }]}><Input placeholder="2026-27" /></Form.Item></Col>
          <Col span={5}><Form.Item name="effectiveFrom" label="Effective from" rules={[{ required: true }]}><DatePicker style={{ width: '100%' }} /></Form.Item></Col>
          <Col span={4}><Form.Item name="effectiveTo" label="Effective to"><DatePicker style={{ width: '100%' }} /></Form.Item></Col>
        </Row>

        <Divider orientation="left">1. Advance / prepaid cash discount</Divider>
        <Typography.Text type="secondary">Full payment <b>before dispatch</b> — discount by the amount paid.</Typography.Text>
        <Row gutter={[12, 12]} style={{ marginTop: 8, marginBottom: 4 }}>
          <Col span={12}>
            <Form.Item name="advanceSlabBasis" label="Match slabs against"
              tooltip="Which order value the prepaid amount slabs above are compared against. Company policy = the basic / taxable value (before GST).">
              <Select options={[
                { value: 'BASIC_TAXABLE_VALUE', label: 'Basic / taxable value (before GST) — recommended' },
                { value: 'GRAND_TOTAL', label: 'Grand total (GST-inclusive)' },
              ]} />
            </Form.Item>
          </Col>
        </Row>
        <SlabList name="advanceCdSlabs" cols={[
          { name: 'minAmount', label: 'Prepaid amount ≥', render: () => <InputNumber {...amountProps} /> },
          { name: 'cdPct', label: 'Discount %', render: () => <InputNumber style={{ width: '100%' }} min={0} max={100} step={0.5} addonAfter="%" /> },
        ]} add={{ minAmount: 500000, cdPct: 5 }} />

        <Divider orientation="left">2. Prompt-payment cash discount</Divider>
        <Typography.Text type="secondary">Paid <b>after delivery</b> — discount by days from invoice date (0 = before/at billing).</Typography.Text>
        <SlabList name="timelineCdSlabs" cols={[
          { name: 'maxDays', label: 'Paid within (days) ≤', render: () => <InputNumber style={{ width: '100%' }} min={0} max={3650} /> },
          { name: 'cdPct', label: 'Discount %', render: () => <InputNumber style={{ width: '100%' }} min={0} max={100} step={0.5} addonAfter="%" /> },
        ]} add={{ maxDays: 45, cdPct: 0 }} />

        <Divider orientation="left">3. Credit terms</Divider>
        <Row gutter={[12, 12]}>
          <Col span={8}><Form.Item name="creditPeriodDays" label="Credit period (days)" rules={[{ required: true }]}><InputNumber style={{ width: '100%' }} min={0} max={3650} /></Form.Item></Col>
          <Col span={8}><Form.Item name="overdueInterestPct" label="Overdue interest (% / month)" rules={[{ required: true }]}><InputNumber style={{ width: '100%' }} min={0} max={100} step={0.5} addonAfter="%" /></Form.Item></Col>
        </Row>

        <Divider orientation="left">4. Turnover discount (TOD)</Divider>
        <Typography.Text type="secondary">Year-end, on annual turnover (per completed lakh). Enter each band's <b>From</b> + rates in any order — on save the bands are auto-sorted and their upper bounds snapped to be contiguous (last band open-ended). Two bands can't start at the same amount.</Typography.Text>
        <SlabList name="todSlabs" wide cols={[
          { name: 'fromAmount', label: 'From (₹)', render: () => <InputNumber {...amountProps} /> },
          { name: 'toAmount', label: 'To (auto · blank = ∞)', render: () => <InputNumber {...amountProps} /> },
          { name: 'basePct', label: 'Base %', render: () => <InputNumber style={{ width: '100%' }} min={0} max={100} step={0.5} /> },
          { name: 'perLakhPct', label: '% / lakh', render: () => <InputNumber style={{ width: '100%' }} min={0} max={100} step={0.5} /> },
          { name: 'capPct', label: 'Cap %', render: () => <InputNumber style={{ width: '100%' }} min={0} max={100} step={0.5} /> },
        ]} add={{ fromAmount: 3000000, toAmount: null, basePct: 25, perLakhPct: 0, capPct: 25 }} />

        <Divider orientation="left">5. Prompt-payment reminders</Divider>
        <Typography.Text type="secondary">Nudge assigned dealers about invoices still inside the cash-discount window — so they pay early and claim the discount. Only dealers with a live opportunity are notified.</Typography.Text>
        <Row gutter={[12, 12]} style={{ marginTop: 10 }} align="bottom">
          <Col span={8}>
            <Form.Item name="remindersEnabled" label="Send reminders to dealers" valuePropName="checked">
              <Switch checkedChildren="On" unCheckedChildren="Off" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="reminderFrequency" label="Frequency">
              <Select options={[
                { value: 'WEEKLY', label: 'Weekly' },
                { value: 'BIWEEKLY', label: 'Every 2 weeks' },
                { value: 'MONTHLY', label: 'Monthly' },
              ]} />
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left">6. Assigned dealers</Divider>
        <Typography.Text type="secondary">The dealers this scheme applies to. Cash discounts (advance &amp; prompt-payment), credit terms and TOD only apply to <b>assigned</b> dealers. Editing here adds/removes assignments on save; recorded acceptances are preserved.</Typography.Text>
        <Form.Item name="assignedRetailerIds" style={{ marginTop: 10 }}>
          <Select
            mode="multiple" allowClear showSearch optionFilterProp="label"
            placeholder="Select dealers this scheme applies to…"
            options={(dealers ?? []).map((r: any) => ({ value: String(r.id), label: `${r.companyName}${r.code ? ` (${r.code})` : ''}` }))}
          />
        </Form.Item>
        <Typography.Text type="secondary" className="text-xs">Tip: use the “Dealers” action on the scheme list to record a dealer’s formal acceptance or download the acceptance PDF.</Typography.Text>

        <Form.Item name="notes" label="Notes" style={{ marginTop: 12 }}><Input.TextArea rows={2} maxLength={2000} placeholder="General terms, remarks…" /></Form.Item>

        <PreviewPanel form={form} />
      </Form>
    </Drawer>
  );
}

// TOD bands partition the turnover axis by their LOWER bound (computeTod only ever uses `From`), so the upper
// bound is really just "the next band's lower bound". On save we therefore auto-sort the bands by From and snap
// each upper bound to the next band's lower bound (the last band stays open-ended). This lets the user enter
// bands in any order and never hand-align the To column. The one thing we can't resolve automatically is two
// bands sharing a lower bound (which one wins is ambiguous) — that is surfaced as a precise error.
function tidyTodBands(rows: any[]): { todSlabs: any[]; error?: string } {
  const raw = (rows ?? []).filter((s: any) => s?.fromAmount != null && s.fromAmount !== '');
  if (!raw.length) return { todSlabs: [] };
  const sorted = [...raw].sort((a, b) => Number(a.fromAmount) - Number(b.fromAmount));
  for (let i = 1; i < sorted.length; i++) {
    if (Number(sorted[i].fromAmount) === Number(sorted[i - 1].fromAmount)) {
      return { todSlabs: sorted, error: `Two TOD bands start at ₹${Number(sorted[i].fromAmount).toLocaleString('en-IN')}. Each band must start higher than the one before — remove or change one.` };
    }
  }
  const snapped = sorted.map((s: any, i: number) => ({ ...s, toAmount: i < sorted.length - 1 ? Number(sorted[i + 1].fromAmount) : null }));
  return { todSlabs: snapped };
}

function SlabList({ name, cols, add, wide }: { name: string; cols: { name: string; label: string; render: () => any }[]; add: any; wide?: boolean }) {
  const span = Math.max(2, Math.floor((wide ? 22 : 20) / cols.length));
  return (
    <Form.List name={name}>
      {(fields, { add: addRow, remove }) => (
        <div>
          {fields.length > 0 && (
            <Row gutter={[12, 12]} style={{ marginBottom: 2 }}>
              {cols.map((c) => <Col key={c.name} span={span}><Typography.Text type="secondary" className="text-xs">{c.label}</Typography.Text></Col>)}
            </Row>
          )}
          {fields.map(({ key, name: fname }) => (
            <Row key={key} gutter={[12, 12]} style={{ marginBottom: 6 }} align="middle">
              {cols.map((c) => (
                <Col key={c.name} span={span}>
                  <Form.Item name={[fname, c.name]} noStyle>{c.render()}</Form.Item>
                </Col>
              ))}
              <Col span={2}><Button variant="danger" size="sm" icon={<Trash2 size={16} />} onClick={() => remove(fname)} aria-label="Remove row" /></Col>
            </Row>
          ))}
          <Button variant="default" onClick={() => addRow(add)} block icon={<Plus size={16} />} style={{ marginTop: 4 }}>Add row</Button>
        </div>
      )}
    </Form.List>
  );
}

function PreviewPanel({ form }: { form: any }) {
  const [inputs, setInputs] = useState<any>({ prepaidAmount: 1000000, daysFromInvoice: 12, annualTurnover: 2500000, overdueAmount: 100000, daysOverdue: 30 });
  const [result, setResult] = useState<any>(null);
  const preview = useMutation({
    mutationFn: () => {
      const v = form.getFieldsValue();
      return request<any>({ url: '/discount-schemes/preview', method: 'POST', data: {
        advanceCdSlabs: (v.advanceCdSlabs ?? []).filter((s: any) => s?.minAmount != null),
        timelineCdSlabs: (v.timelineCdSlabs ?? []).filter((s: any) => s?.maxDays != null),
        todSlabs: (v.todSlabs ?? []).filter((s: any) => s?.fromAmount != null).map((s: any) => ({ ...s, toAmount: s.toAmount ?? null })),
        creditPeriodDays: v.creditPeriodDays ?? 0, overdueInterestPct: v.overdueInterestPct ?? 0, ...inputs,
      } });
    },
    onSuccess: (r) => setResult(r),
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Preview failed'),
  });

  return (
    <Card className="mt-2"
      title={<span><Calculator size={16} /> Preview calculator</span>}
      extra={<Button size="sm" variant="primary" loading={preview.isPending} onClick={() => preview.mutate()}>Calculate</Button>}>
      <Row gutter={[12, 12]}>
        <Col span={5}><div className="text-xs" style={{ color: '#888' }}>Prepaid amount</div><InputNumber {...amountProps} value={inputs.prepaidAmount} onChange={(v) => setInputs({ ...inputs, prepaidAmount: v })} /></Col>
        <Col span={4}><div className="text-xs" style={{ color: '#888' }}>Days from invoice</div><InputNumber style={{ width: '100%' }} min={0} value={inputs.daysFromInvoice} onChange={(v) => setInputs({ ...inputs, daysFromInvoice: v })} /></Col>
        <Col span={5}><div className="text-xs" style={{ color: '#888' }}>Annual turnover</div><InputNumber {...amountProps} value={inputs.annualTurnover} onChange={(v) => setInputs({ ...inputs, annualTurnover: v })} /></Col>
        <Col span={5}><div className="text-xs" style={{ color: '#888' }}>Overdue amount</div><InputNumber {...amountProps} value={inputs.overdueAmount} onChange={(v) => setInputs({ ...inputs, overdueAmount: v })} /></Col>
        <Col span={5}><div className="text-xs" style={{ color: '#888' }}>Days overdue</div><InputNumber style={{ width: '100%' }} min={0} value={inputs.daysOverdue} onChange={(v) => setInputs({ ...inputs, daysOverdue: v })} /></Col>
      </Row>
      {result && (
        <Descriptions size="small" column={4} bordered className="mt-3">
          <Descriptions.Item label="Advance CD">{result.advanceCdPct != null ? `${result.advanceCdPct}%` : '—'}</Descriptions.Item>
          <Descriptions.Item label="Timeline CD">{result.timelineCdPct != null ? `${result.timelineCdPct}%` : '—'}</Descriptions.Item>
          <Descriptions.Item label="TOD">{result.todPct != null ? `${result.todPct}%` : '—'}</Descriptions.Item>
          <Descriptions.Item label="Overdue interest">{result.overdueInterest != null ? inr(result.overdueInterest) : '—'}</Descriptions.Item>
        </Descriptions>
      )}
      <Typography.Paragraph type="secondary" className="text-2xs" style={{ marginTop: 8, marginBottom: 0 }}>
        Indicative — uses the current (unsaved) form. These figures are not yet auto-applied to orders/invoices (Phase 2).
      </Typography.Paragraph>
    </Card>
  );
}

// ─────────────────────────── Assign dealers ───────────────────────────

function AssignDrawer({ scheme, onClose, onChanged }: { scheme: any; onClose: () => void; onChanged: () => void }) {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<string[]>([]);
  const [accepted, setAccepted] = useState(false);
  const open = !!scheme;

  const { data: full } = useQuery({
    queryKey: ['commercial-scheme', scheme?.id],
    queryFn: () => request<any>({ url: `/discount-schemes/${scheme.id}` }),
    enabled: open,
  });
  const { data: retailers } = useQuery({
    queryKey: ['retailers-picker'],
    queryFn: async () => {
      const r: any = await request<any>({ url: '/retailers', params: { pageSize: 1000 } });
      return Array.isArray(r) ? r : (r?.data ?? []);
    },
    enabled: open,
  });

  useEffect(() => { if (open) { setSelected([]); setAccepted(false); } }, [open]);

  const assign = useMutation({
    mutationFn: () => request({ url: `/discount-schemes/${scheme.id}/assign`, method: 'POST', data: {
      assignments: selected.map((rid) => ({ retailerId: rid, accepted })),
    } }),
    onSuccess: () => { message.success('Dealers assigned'); qc.invalidateQueries({ queryKey: ['commercial-scheme', scheme.id] }); setSelected([]); onChanged(); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });
  const removeOne = (retailerId: string) =>
    request({ url: `/discount-schemes/${scheme.id}/assign/${retailerId}`, method: 'DELETE' })
      .then(() => { qc.invalidateQueries({ queryKey: ['commercial-scheme', scheme.id] }); onChanged(); });

  const assignedIds = new Set((full?.assignments ?? []).map((a: any) => String(a.retailerId)));

  return (
    <Drawer open={open} onClose={onClose} width={620} title={scheme ? `Dealers · ${scheme.name}` : ''}>
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Card title="Assign dealers">
          <Select
            mode="multiple" allowClear showSearch optionFilterProp="label" style={{ width: '100%' }} placeholder="Select dealers…"
            value={selected} onChange={setSelected}
            options={(retailers ?? []).filter((r: any) => !assignedIds.has(String(r.id))).map((r: any) => ({ value: String(r.id), label: `${r.companyName}${r.code ? ` (${r.code})` : ''}` }))}
          />
          <Space style={{ marginTop: 10 }}>
            <label><input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} /> Mark as accepted now</label>
            <Button variant="primary" disabled={!selected.length} loading={assign.isPending} onClick={() => assign.mutate()}>Assign {selected.length || ''}</Button>
          </Space>
        </Card>

        <Card title={`Assigned dealers (${full?.assignments?.length ?? 0})`}>
          {(full?.assignments ?? []).length === 0 ? <EmptyState title="No dealers assigned" /> : (
            <Space direction="vertical" style={{ width: '100%' }}>
              {(full?.assignments ?? []).map((a: any) => (
                <div key={a.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f0f0f0', paddingBottom: 6 }}>
                  <span>{a.retailerName ?? `#${a.retailerId}`} {a.status === 'ACCEPTED' ? <Tag tone="green">Accepted</Tag> : <Tag>Assigned</Tag>}</span>
                  <Space size={4}>
                    <Button variant="default" size="sm" icon={<FileText size={16} />} title="Acceptance PDF for this dealer" aria-label="Acceptance PDF for this dealer"
                      onClick={() => openSchemePdf(scheme.id, String(a.retailerId)).catch(() => message.error('Failed to open PDF'))} />
                    <Popconfirm title="Remove this dealer?" onConfirm={() => removeOne(String(a.retailerId))}>
                      <Button variant="danger" size="sm" icon={<Trash2 size={16} />} aria-label="Remove dealer" />
                    </Popconfirm>
                  </Space>
                </div>
              ))}
            </Space>
          )}
        </Card>
      </Space>
    </Drawer>
  );
}
